import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { format, addDays } from 'date-fns';
import { 
  Gift, Calendar, MapPin, Clock, QrCode, Check, AlertCircle,
  ChevronRight, CalendarDays, Ticket, Building2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import ConsumerLayout from '@/components/ConsumerLayout';
import { formatDate, formatTime } from '@/lib/dateUtils';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Voucher Card Component
const VoucherCard = ({ voucher, onViewDetails, onReserve, onRedeem }) => {
  const statusColors = {
    active: 'bg-green-100 text-green-800 border-green-200',
    reserved: 'bg-blue-100 text-blue-800 border-blue-200',
    redeemed: 'bg-gray-100 text-gray-800 border-gray-200',
    expired: 'bg-red-100 text-red-800 border-red-200'
  };
  
  const statusLabels = {
    active: 'Active',
    reserved: 'Reserved',
    redeemed: 'Redeemed',
    expired: 'Expired'
  };
  
  const isUsable = voucher.status === 'active' || voucher.status === 'reserved';
  
  return (
    <Card className={`overflow-hidden ${!isUsable ? 'opacity-75' : ''}`}>
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4 text-white">
        <div className="flex items-start justify-between">
          <div>
            <Badge className={`${statusColors[voucher.status]} border mb-2`}>
              {statusLabels[voucher.status]}
            </Badge>
            <h3 className="text-lg font-bold">{voucher.voucher_title}</h3>
            <p className="text-sm text-white/80 flex items-center gap-1 mt-1">
              <Building2 className="w-4 h-4" />
              {voucher.venue_name}
            </p>
          </div>
          <Gift className="w-8 h-8 text-white/50" />
        </div>
      </div>
      
      <CardContent className="p-4 space-y-4">
        {/* Reservation Info */}
        {voucher.reservation_date && (
          <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
            <p className="text-sm font-medium text-blue-800 dark:text-blue-400 flex items-center gap-2">
              <CalendarDays className="w-4 h-4" />
              Reserved for {formatDate(voucher.reservation_date)}
              {voucher.reservation_time && ` at ${formatTime(voucher.reservation_time)}`}
            </p>
          </div>
        )}
        
        {/* Redemption Info */}
        {voucher.status === 'redeemed' && voucher.redeemed_at && (
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500" />
              Redeemed on {formatDate(voucher.redeemed_at.split('T')[0])}
            </p>
          </div>
        )}
        
        {/* Valid Until */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Valid Until</span>
          <span className={`font-medium ${
            new Date(voucher.valid_until) < new Date() ? 'text-red-500' : 'text-foreground'
          }`}>
            {formatDate(voucher.valid_until)}
          </span>
        </div>
        
        {/* Redemption Code (partially hidden) */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Code</span>
          <span className="font-mono font-medium">
            {voucher.redemption_code.substring(0, 4)}••••{voucher.redemption_code.substring(8)}
          </span>
        </div>
        
        {/* Actions */}
        <div className="flex gap-2 pt-2">
          {isUsable && (
            <>
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => onViewDetails(voucher)}
              >
                View Details
              </Button>
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => onRedeem(voucher)}
              >
                <QrCode className="w-4 h-4 mr-2" />
                Redeem
              </Button>
            </>
          )}
          {!isUsable && (
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => onViewDetails(voucher)}
            >
              View Details
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Redemption Dialog Component
const RedemptionDialog = ({ voucher, open, onClose, onSelfRedeem }) => {
  if (!voucher) return null;
  
  const isRedeemed = voucher.status === 'redeemed';
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader className="text-center">
          <div className="mx-auto w-20 h-20 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center mb-4">
            <Gift className="w-10 h-10 text-purple-600" />
          </div>
          <DialogTitle className="text-2xl">{voucher.voucher_title}</DialogTitle>
          <DialogDescription>
            {isRedeemed ? 'This voucher has been redeemed' : 'Show this to the venue or tap "I\'m at the Venue" to redeem'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Large Redemption Code */}
          <div className="p-6 bg-gray-900 dark:bg-gray-800 rounded-xl text-center">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Redemption Code</p>
            <p className="text-3xl font-mono font-bold text-white tracking-widest">
              {voucher.redemption_code}
            </p>
            {isRedeemed && <p className="text-green-400 text-sm mt-2">Redeemed</p>}
          </div>
          
          {/* Venue Info */}
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="font-bold">{voucher.venue_name}</p>
                <p className="text-sm text-muted-foreground">Present to staff</p>
              </div>
            </div>
          </div>
          
          {/* Reservation Info */}
          {voucher.reservation_date && (
            <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
              <p className="font-medium text-blue-800 dark:text-blue-300">
                Reserved: {formatDate(voucher.reservation_date)}
                {voucher.reservation_time && ` at ${formatTime(voucher.reservation_time)}`}
              </p>
            </div>
          )}
        </div>
        
        <DialogFooter className="flex-col gap-2">
          {!isRedeemed && (
            <Button className="w-full bg-green-600 hover:bg-green-700" onClick={() => onSelfRedeem(voucher.id)}>
              I'm at the Venue — Redeem Now
            </Button>
          )}
          <Button variant="outline" className="w-full" onClick={onClose}>
            {isRedeemed ? 'Close' : 'Done'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
const MyExperiences = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [vouchers, setVouchers] = useState([]);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [showRedemptionDialog, setShowRedemptionDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/auth?redirect=/my-experiences');
      return;
    }
    
    fetchVouchers();
  }, [isAuthenticated, authLoading]);

  const fetchVouchers = async () => {
    try {
      const response = await api.get('/redemptions/my-vouchers');
      setVouchers(response.data.vouchers || []);
    } catch (error) {
      console.error('Failed to fetch vouchers:', error);
      toast.error('Failed to load your experiences');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (voucher) => {
    try {
      const response = await api.get(`/redemptions/my-vouchers/${voucher.id}`);
      setSelectedVoucher(response.data.purchase);
      // Could open a detail modal here
    } catch (error) {
      toast.error('Failed to load voucher details');
    }
  };

  const handleRedeem = (voucher) => {
    setSelectedVoucher(voucher);
    setShowRedemptionDialog(true);
  };

  const handleSelfRedeem = async (purchaseId) => {
    try {
      await api.post(`/redemptions/self-redeem/${purchaseId}`);
      toast.success('Voucher redeemed! The venue has been notified.');
      setShowRedemptionDialog(false);
      setSelectedVoucher(null);
      fetchVouchers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redeem');
    }
  };

  // Filter vouchers by status
  const activeVouchers = vouchers.filter(v => v.status === 'active' || v.status === 'reserved');
  const redeemedVouchers = vouchers.filter(v => v.status === 'redeemed');
  const expiredVouchers = vouchers.filter(v => v.status === 'expired');

  if (authLoading) {
    return (
      <ConsumerLayout hideFooter>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
        </div>
      </ConsumerLayout>
    );
  }

  return (
    <ConsumerLayout>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">My Experiences</h1>
          <p className="text-muted-foreground mt-1">
            View and manage your purchased experiences and vouchers
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
          </div>
        ) : vouchers.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Gift className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">No Experiences Yet</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                You haven't purchased any experiences yet. Browse our marketplace to find amazing deals!
              </p>
              <Button 
                onClick={() => navigate('/marketplace')}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Gift className="w-4 h-4 mr-2" />
                Browse Experiences
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="active" className="flex items-center gap-2">
                <Ticket className="w-4 h-4" />
                Active ({activeVouchers.length})
              </TabsTrigger>
              <TabsTrigger value="redeemed" className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                Redeemed ({redeemedVouchers.length})
              </TabsTrigger>
              <TabsTrigger value="expired" className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Expired ({expiredVouchers.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="active">
              {activeVouchers.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <Gift className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No active experiences</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => navigate('/marketplace')}
                    >
                      Browse Experiences
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {activeVouchers.map((voucher) => (
                    <VoucherCard
                      key={voucher.id}
                      voucher={voucher}
                      onViewDetails={handleViewDetails}
                      onRedeem={handleRedeem}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="redeemed">
              {redeemedVouchers.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <Check className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No redeemed experiences yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {redeemedVouchers.map((voucher) => (
                    <VoucherCard
                      key={voucher.id}
                      voucher={voucher}
                      onViewDetails={handleViewDetails}
                      onRedeem={() => {}}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="expired">
              {expiredVouchers.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No expired experiences</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {expiredVouchers.map((voucher) => (
                    <VoucherCard
                      key={voucher.id}
                      voucher={voucher}
                      onViewDetails={handleViewDetails}
                      onRedeem={() => {}}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
      
      {/* Redemption Dialog */}
      <RedemptionDialog
        voucher={selectedVoucher}
        open={showRedemptionDialog}
        onClose={() => setShowRedemptionDialog(false)}
        onSelfRedeem={handleSelfRedeem}
      />
      
    </ConsumerLayout>
  );
};

export default MyExperiences;
